import { z } from 'zod';
import { currencyCodeSchema, moneySchema, priceSourceSchema } from './prices.js';
import { decimalStringSchema, isoDateTimeSchema, positiveDecimalStringSchema, uuidSchema, localeSchema, } from './common.js';
import { groceryCategorySchema, shoppingListStatusSchema } from './enums.js';
const nutriScoreSchema = z.enum(['A', 'B', 'C', 'D', 'E']);
export const shoppingRecipeRefSchema = z.object({
    recipeId: uuidSchema,
    recipeTitle: z.string(),
    ingredientId: uuidSchema.nullable(),
    /** Servings used when the ingredient was added, so quantities can be recalculated. */
    servings: decimalStringSchema,
    originalServings: decimalStringSchema,
    quantity: decimalStringSchema.nullable(),
    unitCode: z.string().nullable(),
});
/** How often a reminder comes back. */
export const SHOPPING_REMINDER_REPEATS = ['NONE', 'DAILY', 'WEEKLY', 'MONTHLY'];
export const shoppingReminderRepeatSchema = z.enum(SHOPPING_REMINDER_REPEATS);
/**
 * "Remind me" on a list or one of its items. Goes to the assignee, or to everybody who can see
 * the list – as a notification, and as push where a device subscribed.
 */
export const shoppingReminderSchema = z.object({
    id: uuidSchema,
    listId: uuidSchema,
    itemId: uuidSchema.nullable(),
    /** The time it was set for, in `timeZone`; a repeat counts on from here. */
    remindAt: isoDateTimeSchema,
    repeat: shoppingReminderRepeatSchema,
    /** IANA zone, e.g. Europe/Vienna – a weekly reminder keeps its wall-clock time across DST. */
    timeZone: z.string(),
    /** Who is reminded; null = everybody with access to the list. */
    assignee: z.object({ id: uuidSchema, displayName: z.string() }).nullable(),
    /** The next firing; null once a one-off has fired. */
    nextAt: isoDateTimeSchema.nullable(),
    createdById: uuidSchema.nullable(),
});
export const shoppingReminderWriteSchema = z.object({
    remindAt: isoDateTimeSchema,
    repeat: shoppingReminderRepeatSchema.default('NONE'),
    timeZone: z.string().trim().min(1).max(64),
    /** A member of the list's household (or its owner); null = everybody with access. */
    assigneeId: uuidSchema.nullable().optional(),
});
export const shoppingListItemSchema = z.object({
    id: uuidSchema,
    listId: uuidSchema,
    name: z.string(),
    normalizedName: z.string(),
    quantity: decimalStringSchema.nullable(),
    quantityMax: decimalStringSchema.nullable(),
    quantityText: z.string().nullable(),
    unitCode: z.string().nullable(),
    unitText: z.string().nullable(),
    /** Formatted amount only, e.g. "2 kg" or "to taste"; clients render it together with `name`. */
    display: z.string(),
    groceryCategory: groceryCategorySchema,
    checked: z.boolean(),
    note: z.string().nullable(),
    recipeRefs: z.array(shoppingRecipeRefSchema),
    position: z.number().int(),
    /** Catalog link (docs/finished/ingredient-catalog-spec.md 6.1); absent when the catalog is disabled. */
    ingredientId: uuidSchema.nullable().optional(),
    reminder: shoppingReminderSchema.nullable().optional(),
    /** Preferred product chosen by the user, with what the list needs to render it. */
    product: z
        .object({
        id: uuidSchema,
        name: z.string(),
        brand: z.string().nullable(),
        barcode: z.string().nullable(),
        nutriScore: nutriScoreSchema.nullable(),
        thumbnailUrl: z.string().nullable(),
    })
        .nullable()
        .optional(),
    /**
     * What was paid for one sales unit on the last trip (docs/partial/shopping-in-store-spec.md
     * 6.3); absent on older servers.
     */
    price: moneySchema.nullable().optional(),
    priceCurrency: currencyCodeSchema.nullable().optional(),
    priceSource: priceSourceSchema.nullable().optional(),
    /** "Bought another one": how many were bought – not the wanted quantity. */
    purchasedCount: z.number().int().min(1).optional(),
    /** When and by whom the item was checked off / added (shopping-list-extensions-spec 6.3). */
    checkedAt: isoDateTimeSchema.nullable().optional(),
    checkedById: uuidSchema.nullable().optional(),
    checkedByName: z.string().nullable().optional(),
    /** Where it was checked off, when the device had a fresh position (a scan in the store). */
    checkedLat: z.number().nullable().optional(),
    checkedLng: z.number().nullable().optional(),
    addedById: uuidSchema.nullable().optional(),
    updatedAt: isoDateTimeSchema,
});
export const shoppingListSchema = z.object({
    id: uuidSchema,
    ownerId: uuidSchema,
    householdId: uuidSchema.nullable(),
    name: z.string(),
    /** The kind of list ("Lebensmittel", "Drogerie", …), one of the user's list types; null = none. */
    type: z.string().nullable().optional(),
    status: shoppingListStatusSchema,
    itemCount: z.number().int(),
    checkedCount: z.number().int(),
    version: z.number().int(),
    /** The reminder set on the list itself (items carry their own). */
    reminder: shoppingReminderSchema.nullable().optional(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const shoppingListDetailSchema = shoppingListSchema.extend({
    items: z.array(shoppingListItemSchema),
    /** After adding a recipe: ingredients left out because they are always in stock (pantry staples). */
    skippedStaples: z.array(z.string()).optional(),
});
export const shoppingListWriteSchema = z.object({
    /** Client-generated id for lists created offline (also the idempotency key). */
    id: uuidSchema.optional(),
    name: z.string().trim().min(1).max(120),
    type: z.string().trim().max(40).nullable().optional(),
    householdId: uuidSchema.nullable().optional(),
});
export const shoppingListUpdateSchema = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    /** null removes the type. */
    type: z.string().trim().max(40).nullable().optional(),
    status: shoppingListStatusSchema.optional(),
    /** Owner only: share the list with a household, or make it personal again (null). */
    householdId: uuidSchema.nullable().optional(),
    version: z.number().int().nonnegative(),
});
export const shoppingItemWriteSchema = z.object({
    /**
     * Client-generated id, used when the add creates a new item: an item added offline keeps
     * its id, so a check-off tapped right after it never points at a row that was renamed.
     */
    id: uuidSchema.optional(),
    name: z.string().trim().min(1).max(200),
    quantity: decimalStringSchema.nullable().optional(),
    quantityMax: decimalStringSchema.nullable().optional(),
    quantityText: z.string().trim().max(80).nullable().optional(),
    unitCode: z.string().max(32).nullable().optional(),
    unitText: z.string().max(40).nullable().optional(),
    groceryCategory: groceryCategorySchema.optional(),
    note: z.string().trim().max(500).nullable().optional(),
    /** Preferred product; must be visible to the list's household. */
    productId: uuidSchema.nullable().optional(),
    /**
     * When the add re-activates a bought item: NEW (default) uses the new quantity, KEEP the one
     * the item had. The client decides (user setting); the server never asks.
     */
    reactivateQuantity: z.enum(['NEW', 'KEEP']).optional(),
});
export const shoppingItemUpdateSchema = shoppingItemWriteSchema.partial().extend({
    checked: z.boolean().optional(),
    position: z.number().int().min(0).optional(),
    /** What was paid; null removes it. The currency defaults to the writer's. */
    price: moneySchema.nullable().optional(),
    priceCurrency: currencyCodeSchema.optional(),
    /** "Bought another one" – only meaningful on a checked item. */
    purchasedCount: z.number().int().min(1).max(99).optional(),
    /** Explicit catalog link chosen by the user; null = "no ingredient". */
    ingredientId: uuidSchema.nullable().optional(),
    /**
     * Time of the tap for check/uncheck. A change queued offline does not overwrite a newer
     * change by someone else: the last tap wins, not the last sync.
     */
    checkedAt: isoDateTimeSchema.optional(),
    /** The device's position at the check-off, if the app had one; both or neither. */
    checkedLat: z.number().min(-90).max(90).nullable().optional(),
    checkedLng: z.number().min(-180).max(180).nullable().optional(),
    /** `updatedAt` the edit was based on; a stale text/quantity edit answers 409. */
    baseUpdatedAt: isoDateTimeSchema.optional(),
});
export const addRecipeToShoppingListRequestSchema = z.object({
    recipeId: uuidSchema,
    /** Servings to scale to before adding. Defaults to the recipe's own servings. */
    servings: positiveDecimalStringSchema.optional(),
    /** Subset of ingredient ids; omit for the entire recipe. */
    ingredientIds: z.array(uuidSchema).max(200).optional(),
    includeOptional: z.boolean().default(false),
    subtractPantry: z.boolean().default(false),
});
export const shoppingBulkRequestSchema = z.object({
    action: z.enum(['CHECK_ALL', 'UNCHECK_ALL', 'REMOVE_CHECKED', 'RECALCULATE']),
});
// ---------------------------------------------------------------------------
// Shopping list extensions (docs/partial/shopping-list-extensions-spec.md)
// ---------------------------------------------------------------------------
export const SHOPPING_MAX_BOUGHT_ITEMS = 50;
export const SHOPPING_HISTORY_MAX_ENTRIES = 500;
export const SHOPPING_BATCH_MAX_ITEMS = 50;
export const SHOPPING_CLIENT_ID_HEADER = 'x-client-id';
export const SHOPPING_ITEM_OUTCOME_HEADER = 'x-item-outcome';
export const shoppingAddOutcomeSchema = z.enum(['CREATED', 'MERGED', 'REACTIVATED', 'UNCHANGED']);
export const shoppingAddResultSchema = z.object({
    outcome: shoppingAddOutcomeSchema,
    item: shoppingListItemSchema,
});
export const shoppingBatchAddRequestSchema = z.object({
    items: z.array(shoppingItemWriteSchema).min(1).max(SHOPPING_BATCH_MAX_ITEMS),
});
export const shoppingBatchAddResponseSchema = z.object({
    results: z.array(shoppingAddResultSchema),
    list: shoppingListSchema,
});
export const shoppingReactivationSchema = z.enum(['REPLACE', 'ASK']);
/** How a list is laid out: by department (in the store's order), by name, or as entered. */
export const SHOPPING_LIST_SORTS = ['DEPARTMENT', 'ALPHABETICAL', 'ADDED'];
export const shoppingListSortSchema = z.enum(SHOPPING_LIST_SORTS);
export const shoppingPreferencesSchema = z.object({
    /** The layout of the open items on a list; the user's choice, remembered. */
    listSort: shoppingListSortSchema.default('DEPARTMENT'),
    /**
     * The kinds of lists the user keeps ("Lebensmittel", "Drogerie", "Diverses" …), in their own
     * words; empty = the app's defaults in the user's language.
     */
    listTypes: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
    /** Quantity of a re-activated item: use the new one, or ask. */
    reactivation: shoppingReactivationSchema.default('REPLACE'),
    /** Add dictated items automatically after a short countdown. */
    handsFree: z.boolean().default(false),
    /** Let an AI model split doubtful dictations (needs AI consent, online only). */
    aiVoiceParsing: z.boolean().default(false),
    /** Shopping in the store (docs/partial/shopping-in-store-spec.md 6.1): a dictation ends by itself. */
    voiceAutoStop: z.boolean().default(true),
    /** Seconds without speech that end a dictation. */
    voiceSilenceSeconds: z.union([z.literal(2), z.literal(3), z.literal(5)]).default(2),
    /** A scanned product that matches a list item exactly: ask, or check it off at once. */
    scanCheckOff: z.enum(['ASK', 'AUTO']).default('ASK'),
    /** Prices on list items and the estimate in the list footer. */
    showPrices: z.boolean().default(true),
    /** How long the photos of a receipt are kept; 0 = deleted when the receipt is confirmed. */
    receiptImageRetentionDays: z
        .union([z.literal(0), z.literal(7), z.literal(30), z.literal(90), z.literal(365)])
        .default(30),
});
export const RECEIPT_IMAGE_RETENTION_CHOICES = [0, 7, 30, 90, 365];
export const shoppingHistoryEntrySchema = z.object({
    id: uuidSchema,
    householdId: uuidSchema.nullable(),
    normalizedName: z.string(),
    displayName: z.string(),
    groceryCategory: groceryCategorySchema,
    lastQuantity: decimalStringSchema.nullable(),
    lastUnitCode: z.string().nullable(),
    lastUnitText: z.string().nullable(),
    useCount: z.number().int(),
    lastUsedAt: isoDateTimeSchema,
    hidden: z.boolean(),
    /** Learned pairing: the product this household scanned for that name (absent on older servers). */
    lastProductId: uuidSchema.nullable().optional(),
    updatedAt: isoDateTimeSchema,
});
export const shoppingSuggestionQuerySchema = z.object({
    q: z.string().trim().max(120).default(''),
    listId: uuidSchema,
    limit: z.coerce.number().int().min(1).max(20).default(8),
});
export const shoppingSuggestionSchema = z.object({
    /** History entry id, or null for a suggestion that comes from the ingredient catalog. */
    historyId: uuidSchema.nullable(),
    name: z.string(),
    normalizedName: z.string(),
    groceryCategory: groceryCategorySchema,
    quantity: decimalStringSchema.nullable(),
    unitCode: z.string().nullable(),
    unitText: z.string().nullable(),
    /** A bought item with this name is on the list: adding re-activates it. */
    reactivates: z.boolean(),
    source: z.enum(['HISTORY', 'CATALOG']),
});
// Device copy ---------------------------------------------------------------
export const shoppingSyncQuerySchema = z.object({ since: z.string().max(200).optional() });
export const shoppingSyncResponseSchema = z.object({
    /** The device drops its copy first (first sync, stale or foreign cursor). */
    full: z.boolean(),
    lists: z.array(shoppingListSchema),
    items: z.array(shoppingListItemSchema),
    history: z.array(shoppingHistoryEntrySchema),
    tombstones: z.array(z.object({
        entity: z.enum(['list', 'item', 'history']),
        id: uuidSchema,
        listId: uuidSchema.nullable(),
    })),
    /** Every active list the reader can access; anything else is removed from the device. */
    accessibleListIds: z.array(uuidSchema),
    preferences: shoppingPreferencesSchema,
    cursor: z.string(),
    hasMore: z.boolean(),
    syncedAt: isoDateTimeSchema,
});
// Live updates ---------------------------------------------------------------
export const shoppingPresenceUserSchema = z.object({ userId: uuidSchema, displayName: z.string() });
const eventBase = {
    /** Version of the list after the change; a gap means: refetch. */
    version: z.number().int(),
    /** Echo of the X-Client-Id header of the request that caused the change. */
    clientId: z.string().nullable(),
};
export const shoppingEventSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('item.upserted'), item: shoppingListItemSchema, ...eventBase }),
    z.object({ type: z.literal('item.removed'), itemId: uuidSchema, ...eventBase }),
    z.object({ type: z.literal('items.bulk'), items: z.array(shoppingListItemSchema), ...eventBase }),
    z.object({ type: z.literal('list.updated'), list: shoppingListSchema, ...eventBase }),
    z.object({ type: z.literal('list.deleted'), ...eventBase }),
    z.object({ type: z.literal('access.revoked') }),
    z.object({ type: z.literal('presence'), users: z.array(shoppingPresenceUserSchema) }),
    z.object({ type: z.literal('heartbeat') }),
]);
// Voice ------------------------------------------------------------------------
export const shoppingUtteranceRequestSchema = z.object({
    text: z.string().trim().min(1).max(500),
    language: localeSchema.optional(),
});
export const shoppingUtteranceItemSchema = z.object({
    name: z.string().trim().min(1).max(200),
    quantity: decimalStringSchema.nullable(),
    unit: z.string().max(40).nullable(),
});
export const shoppingUtteranceResponseSchema = z.object({
    items: z.array(shoppingUtteranceItemSchema).max(SHOPPING_BATCH_MAX_ITEMS),
});
/** Input stored on the AIGeneration row for SHOPPING_UTTERANCE_PARSE: the transcript only. */
export const aiShoppingUtteranceInputSchema = z.object({
    text: z.string().max(500),
    language: localeSchema.default('de'),
});
//# sourceMappingURL=shopping.js.map